// Package Imports

import { Button, Popconfirm } from 'antd';
import { CopyOutlined, DeleteOutlined } from '@ant-design/icons';

// Project Imports

import PrivateRouteConst from 'core/common/privateRouteConstant';
import replacePipe from 'core/utils/replacePipe';
import openNotificationWithIcon from 'core/utils/notification';
import constants from 'core/constants/Constants';
import apiEndpoint from 'core/common/apiEndPoints';

export default function getRenderRuleGroupDeleteButton(
  deleteRuleAndRuleGroup,
  pageType,
  rulePlanDataObj,
  fetchData,
  checkPermission,
  userDetails,
  t
) {
  function renderRuleGroupDeleteButton(row) {
    if (
      checkPermission(PrivateRouteConst.RULE_GROUPS_DELETE) ||
      (checkPermission(PrivateRouteConst.SELF_RULE_GROUPS_DELETE) &&
        row?.created_by === userDetails?.unique_id)
    ) {
      return (
        <Popconfirm
          title={
            row?.used_in_unification_calculation
              ? t('ruleGroup.messages.deactivateRuleGroup')
              : t('ruleGroup.messages.deleteRuleGroup')
          }
          okText={
            row?.used_in_unification_calculation
              ? t('buttons.deactivate')
              : t('buttons.delete')
          }
          okType="danger"
          key="delete"
          onCancel={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onConfirm={(e) => {
            e.stopPropagation();
            e.preventDefault();
            deleteRuleAndRuleGroup(
              {
                resource: `${pageType === 'Show' && rulePlanDataObj?.view_unique_id ? apiEndpoint.ruleGroup : apiEndpoint.ruleGroupDraft}`,
                id: row?.unique_id,
                successNotification: false,
              },
              {
                onError: (error) => {
                  const message =
                    replacePipe(error?.message) ||
                    t('notifications.deleteError');
                  return {
                    message: 'Error Message',
                    description: message,
                    type: 'success',
                  };
                },
                onSuccess: async (data) => {
                  await fetchData();
                  const message =
                    data?.data?.message || t(`notifications.deleteSuccess`);
                  openNotificationWithIcon(
                    constants.success,
                    t('notifications.mutationSuccess'),
                    message
                  );
                },
              }
            );
          }}
        >
          <Button
            danger
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            icon={<DeleteOutlined />}
            disabled={pageType === 'Show' || !row?.rule_group_is_active}
          />
        </Popconfirm>
      );
    }
    return null;
  }
  return renderRuleGroupDeleteButton;
}

export function getRenderRuleDeleteButton(
  deleteRuleAndRuleGroup,
  pageType,
  rulePlanDataObj,
  fetchData,
  checkPermission,
  userDetails,
  t
) {
  function renderRuleDeleteButton(row) {
    if (
      checkPermission(PrivateRouteConst.RULES_DELETE) ||
      (checkPermission(PrivateRouteConst.SELF_RULES_DELETE) &&
        row?.created_by === userDetails?.unique_id)
    ) {
      return (
        <Popconfirm
          title={
            row?.used_in_unification_calculation
              ? t('rules.messages.deactivateRule')
              : t('rules.messages.deleteRule')
          }
          okText={
            row?.used_in_unification_calculation
              ? t('buttons.deactivate')
              : t('buttons.delete')
          }
          okType="danger"
          key="delete"
          onCancel={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onConfirm={(e) => {
            e.stopPropagation();
            e.preventDefault();
            deleteRuleAndRuleGroup(
              {
                resource: `${pageType === 'Show' && rulePlanDataObj?.view_unique_id ? apiEndpoint.rules : apiEndpoint.rulesDraft}`,
                id: row?.unique_id,
                successNotification: false,
              },
              {
                onError: (error) => {
                  const message =
                    replacePipe(error?.message) ||
                    t('notifications.deleteError');
                  return {
                    message: 'Error Message',
                    description: message,
                    type: 'success',
                  };
                },
                onSuccess: async (data) => {
                  await fetchData();
                  const message =
                    data?.data?.message || t(`notifications.deleteSuccess`);
                  openNotificationWithIcon(
                    constants.success,
                    t('notifications.mutationSuccess'),
                    message
                  );
                },
              }
            );
          }}
        >
          <Button
            danger
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            icon={<DeleteOutlined />}
            disabled={pageType === 'Show' || !row?.rule_is_active}
          />
        </Popconfirm>
      );
    }
    return null;
  }
  return renderRuleDeleteButton;
}

export function getRenderCopyButton(
  setCopyModalRow,
  checkPermission,
  setCopyModalVisible,
  copyModalForm,
  setIsCopyModalSubmitDisabled,
  pageType
) {
  function renderCopyButton(row) {
    if (checkPermission(PrivateRouteConst.RULE_PLANS_DELETE)) {
      return (
        <div>
          <Button
            type="primary"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setCopyModalRow(row);
              setCopyModalVisible(true);
              copyModalForm.resetFields();
              // Set default rule_plan_name in form to empty or with a suggestion
              copyModalForm.setFieldsValue({
                rule_name: '',
                rule_group_id: null,
              });
              setIsCopyModalSubmitDisabled(true);
            }}
            disabled={pageType === 'Show'}
          >
            <CopyOutlined />
          </Button>
        </div>
      );
    }
    return null;
  }
  return renderCopyButton;
}

export function getFilterRules(rulesDetails, setRuleGroupList) {
  // eslint-disable-next-line consistent-return
  function filterRules(searchString: string) {
    const data = rulesDetails?.rule_group_list || [];
    if (searchString.trim() === '') {
      setRuleGroupList(data);
      return;
    }
    const filteredData = data
      .map((ruleGroup) => {
        const filteredRules = ruleGroup.rule_list.filter((rule) =>
          rule.rule_name.toLowerCase().includes(searchString.toLowerCase())
        );
        const isGroupNameMatch = ruleGroup.rule_group_name
          .toLowerCase()
          .includes(searchString.toLowerCase());
        if (filteredRules.length > 0 || isGroupNameMatch) {
          return {
            ...ruleGroup,
            rule_list:
              filteredRules.length > 0 ? filteredRules : ruleGroup.rule_list,
          };
        }
        return null;
      })
      .filter((ruleGroup) => ruleGroup !== null);
    setRuleGroupList(filteredData);
  }
  return filterRules;
}
